import React, { ReactElement } from 'react';

import { getFilterDates, Route, shouldRenderMobile } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HpmsCustomContentCollapseEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnOpen } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { Button, FetchAPI, FilterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { ButtonTheme, Color, Placement } from '@philips-emr/tws-constants';
import Controller, { OnBeforeFetch, OnClear, OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { ClearEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';

import { HistoricoRequisicaoConsultaAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import './historico-requisicao-consulta-controller.css';

interface Data {
  nrSeqRequisicao: number;
  ieEstagio: number;
}

interface Guia {
  nrSeqGuia: number;
  nrSeqRequisicao: number;
}

function getStamp(ieEstagio: number): ReactElement | null {
  let stampProps = null;

  const label = `@stipulator.estagio-requisicao/${ieEstagio}`;

  switch (ieEstagio) {
    case 1:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
      break;
    case 2:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case 3:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.CANCELED_OUTLINE, label };
      break;
    case 4:
      stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
      break;
    case 5:
      stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
      break;
    case 6:
      stampProps = {
        className: 'c-stamp-custom-color-purple__historico-requisicao',
        icon: StampIcon.CHECK,
        label,
      };
      break;
    case 7:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case 8:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label };
      break;
    case 9:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case 10:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label };
      break;

    default:
      return null;
  }

  return <Stamp {...stampProps} />;
}

@Controller({ fragment: FragmentNames.HISTORICO_REQUISICAO_CONSULTA })
export default class HistoricoRequisicaoConsultaController extends BaseController {

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnBeforeFetch(Aliases.FETCH_CONSULTA)
  public onBeforeFetchConsulta(event: FetchEvent): void {
    const api = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && api.setParameters({ ...api.getParameters(), nrSeqSubestipulante });
    applicationContext.openModule('historico-requisicao');
  }

  @OnSubmit(Aliases.FILTER_CONSULTA)
  public onSubmitFilter(event: SubmitEvent): void {
    const { record, target } = event;
    const api = target as unknown as FilterAPI;

    if (record && record.dtInicio && record.dtFim) {
      return;
    }

    const { dtInicio, dtFim } = getFilterDates('dtInicio', 'dtFim', 3);
    !record.dtInicio && (record.dtInicio = dtInicio);
    !record.dtFim && (record.dtFim = dtFim);

    api.setData({});
    api.setData({ ...api.getRecord(), dtInicio: record.dtInicio, dtFim: record.dtFim });
  }

  @OnClear(Aliases.FILTER_CONSULTA)
  public onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;
    api.setData({});
    api.setData(getFilterDates());
  }

  private getRequisicao(nrSequencia: number): void {
    HistoryService.push(Route.HISTORICO_REQUISICAO, { nrSequencia });
  }

  private getAutorizacao(nrSeqGuia: number, nrSequencia: number): void {
    HistoryService.push(Route.HISTORICO_REQUISICAO_HISTORICO_AUTORIZACAO, { nrSeqGuia, nrSequencia });
  }

  @HPMSOnOpen(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPSE)
  public onOpenRenderContent(event: HpmsCustomContentCollapseEvent<Data>): void {
    if (!event || !event.data) {
      return;
    }

    const { data, onOpenRender } = event;
    onOpenRender({ parameters: { nrSeqRequisicao: data.nrSeqRequisicao } });
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_ACTION_COLLAPSE)
  public onOpenRenderActionContentCollapse(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSeqRequisicao, nrSeqGuia } = data as unknown as Guia;

    this.renderButton(render, 0, () => this.getAutorizacao(nrSeqGuia, nrSeqRequisicao));
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_STAMP)
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieEstagio } = data as Data;

    if (shouldRenderMobile()) {
      return;
    }

    this.renderStamp(render, ieEstagio);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_STAMP_MOB)
  public onDynamicContentRenderMob(event: RenderEvent): void {
    const { data, render } = event;
    const { ieEstagio } = data as Data;

    if (!shouldRenderMobile()) {
      return;
    }

    this.renderStamp(render, ieEstagio);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_ACTION)
  public onDynamicContentRenderActionDesk(event: RenderEvent): void {
    const { data, render } = event;
    const { nrSeqRequisicao } = data as Data;

    this.renderButton(render, nrSeqRequisicao);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_MOB)
  public onDynamicContentRenderActionMob(event: RenderEvent): void {
    const { data, render } = event;
    const { nrSeqRequisicao } = data as Data;

    if (!shouldRenderMobile()) {
      return;
    }

    this.renderButton(render, nrSeqRequisicao);
  }

  private renderButton(render: Function, nrSeqRequisicao: number, onClick: Function = () => this.getRequisicao(nrSeqRequisicao)): void {
    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.LEFT}
        onClick={() => onClick()}
        className="c-historico-requisicao-consulta__custom-action-button"
      />
    ));
  }

  private renderStamp(render: Function, ieEstagio: number): void {
    const stamp = getStamp(ieEstagio);

    if (stamp) {
      render((): ReactElement => stamp);
    }
  }

}
